import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Drawer,
  useMediaQuery,
  useTheme,
  Fab,
  Badge
} from '@mui/material';
import {
  Search as SearchIcon,
  Menu as MenuIcon,
  ArrowBack as ArrowBackIcon,
  Message as MessageIcon
} from '@mui/icons-material';
import {
  setActiveConversation,
  searchMessages,
  clearSearch,
  markConversationAsRead,
  selectFilteredConversations,
  selectTotalUnreadCount
} from '../redux/slices/messagesSlice';
import ConversationList from '../components/ConversationList';
import ChatWindow from '../components/ChatWindow';
import MessageInput from '../components/MessageInput';
import '../styles/Messages.css';

const Messages = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  
  const dispatch = useDispatch();
  const {
    activeConversation,
    searchQuery,
    currentUser,
    loading
  } = useSelector(state => state.messages);

  // Utiliser les sélecteurs pour les conversations filtrées et le nombre de messages non lus
  const filteredConversations = useSelector(selectFilteredConversations);
  const totalUnreadCount = useSelector(selectTotalUnreadCount);

  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [showConversationList, setShowConversationList] = useState(!isMobile);

  useEffect(() => {
    if (!isMobile) {
      setShowConversationList(true);
      setMobileDrawerOpen(false);
    } else {
      setShowConversationList(!activeConversation);
    }
  }, [isMobile, activeConversation]);

  const handleConversationSelect = (conversation) => {
    dispatch(setActiveConversation(conversation));
    dispatch(markConversationAsRead({ conversationId: conversation.id }));
    
    if (isMobile) {
      setShowConversationList(false);
      setMobileDrawerOpen(false);
    }
  };

  const handleSearchChange = (event) => {
    const query = event.target.value;
    if (query === '') {
      dispatch(clearSearch());
    } else {
      dispatch(searchMessages(query));
    }
  };

  const handleBackToList = () => {
    if (isMobile) {
      dispatch(setActiveConversation(null));
      setShowConversationList(true);
    }
  };

  const toggleMobileDrawer = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const renderConversationListContent = () => (
    <Box className="conversation-list-container">
      <Box className="conversation-list-header">
        <Typography variant="h5" className="messages-title">
          Messages
          {totalUnreadCount > 0 && (
            <Badge 
              badgeContent={totalUnreadCount} 
              color="primary" 
              className="unread-badge"
            >
              <MessageIcon />
            </Badge>
          )}
        </Typography>
        
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Rechercher une conversation..."
          value={searchQuery}
          onChange={handleSearchChange}
          className="search-field"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      
      <ConversationList
        conversations={filteredConversations}
        activeConversation={activeConversation}
        onConversationSelect={handleConversationSelect}
        currentUser={currentUser}
      />
    </Box>
  );

  const renderChatContent = () => (
    <Box className="chat-container">
      {activeConversation ? (
        <>
          {isMobile && (
            <Box className="mobile-chat-header">
              <IconButton onClick={handleBackToList} className="back-button">
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" className="chat-title">
                {activeConversation.participants
                  .find(p => p.id !== currentUser.id)?.name}
              </Typography>
            </Box>
          )}
          
          <ChatWindow
            conversation={activeConversation}
            currentUser={currentUser}
          />
          
          <MessageInput
            conversationId={activeConversation.id}
            currentUser={currentUser}
          />
        </>
      ) : (
        <Box className="no-conversation-selected">
          <MessageIcon className="no-conversation-icon" />
          <Typography variant="h6" className="no-conversation-text">
            Sélectionnez une conversation pour commencer
          </Typography>
          <Typography variant="body2" className="no-conversation-subtitle">
            Choisissez une conversation dans la liste pour voir vos messages
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <Helmet>
        <title>Messages - Mission Sport</title>
        <meta 
          name="description" 
          content="Espace de messagerie interne entre organisations sportives et professionnels du sport sur Mission Sport." 
        />
        <meta name="keywords" content="messages, messagerie, sport, organisations, professionnels" />
      </Helmet>

      <Box className="messages-page">
        {isMobile ? (
          // Vue mobile : soit la liste, soit le chat
          <Box className="mobile-messages-container">
            {showConversationList ? (
              renderConversationListContent()
            ) : (
              renderChatContent()
            )}
          </Box>
        ) : (
          // Vue desktop/tablet : deux colonnes
          <Grid container className="desktop-messages-container">
            <Grid 
              item 
              xs={12} 
              md={4} 
              lg={3} 
              className="conversation-list-grid"
            >
              <Paper className="conversation-list-paper" elevation={1}>
                {renderConversationListContent()}
              </Paper>
            </Grid>
            
            <Grid 
              item 
              xs={12} 
              md={8} 
              lg={9} 
              className="chat-grid"
            >
              <Paper className="chat-paper" elevation={1}>
                {renderChatContent()}
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Drawer pour tablette si nécessaire */}
        {isTablet && (
          <>
            <Fab
              color="primary"
              className="tablet-menu-fab"
              onClick={toggleMobileDrawer}
            >
              <MenuIcon />
            </Fab>
            
            <Drawer
              anchor="left"
              open={mobileDrawerOpen}
              onClose={toggleMobileDrawer}
              className="tablet-drawer"
            >
              <Box className="drawer-content">
                {renderConversationListContent()}
              </Box>
            </Drawer>
          </>
        )}
      </Box>
    </>
  );
};

export default Messages;